import { createSlice } from "@reduxjs/toolkit";
import {
  getWarningLettersThunk,
  getWarningLetterByIdThunk,
  getWarningLettersByPKSThunk,
  getWarningLettersByMitraThunk,
  createWarningLetterThunk,
  updateWarningLetterThunk,
  deleteWarningLetterThunk,
  issueWarningLetterThunk,
  deliverWarningLetterThunk,
  acknowledgeWarningLetterThunk,
  resolveWarningLetterThunk,
  escalateWarningLetterThunk,
} from "state/action/warning_letter";

// ── Helper ────────────────────────────────────────────────────────────────────
const asyncState = (data = null) => ({
  data,
  isFulfilled: false,
  isPending: false,
  isRejected: false,
  err: null,
});
const pending    = (s) => ({ ...s, isPending: true,  isFulfilled: false, isRejected: false, err: null });
const fulfilled  = (s, data) => ({ ...s, isPending: false, isFulfilled: true, data });
const rejected   = (s, err)  => ({ ...s, isPending: false, isRejected: true, err });

// ── Initial form ─────────────────────────────────────────────────────────────
export const initialWarningLetterForm = {
  pks_id: "",
  mitra: "",
  warning_level: 1,         // 1 = SP1, 2 = SP2, 3 = SP3
  violation_type: "",       // "late_payment" | "late_performance" | "contract_breach" | "other"
  violation_description: "",
  violation_date: "",
  related_invoice: "",
  related_penalty: "",
  required_actions: [],     // [{ action, deadline }]
  resolution_deadline: "",
  consequences: "",
  notes: "",
};

// ── Initial state ─────────────────────────────────────────────────────────────
const initialState = {
  getWarningLetters:      asyncState(),
  getWarningLettersByPKS: asyncState(),
  getWarningLettersByMitra: asyncState(),
  getWarningLetterById:   asyncState(),
  createWarningLetter:    asyncState(),
  updateWarningLetter:    asyncState(),
  deleteWarningLetter:    asyncState(),
  issueWarningLetter:     asyncState(),
  deliverWarningLetter:   asyncState(),
  acknowledgeWarningLetter: asyncState(),
  resolveWarningLetter:   asyncState(),
  escalateWarningLetter:  asyncState(),

  warningLetterForm: { ...initialWarningLetterForm },
  selectedWarningLetter: null,
};

// ── Slice ─────────────────────────────────────────────────────────────────────
export const warningLetterSlice = createSlice({
  name: "warningLetter",
  initialState,
  reducers: {
    setWarningLetterForm: (state, action) => {
      state.warningLetterForm = action.payload;
    },
    updateWarningLetterFormField: (state, action) => {
      const { field, value } = action.payload;
      state.warningLetterForm[field] = value;
    },
    resetWarningLetterForm: (state) => {
      state.warningLetterForm = { ...initialWarningLetterForm };
    },
    setSelectedWarningLetter: (state, action) => {
      state.selectedWarningLetter = action.payload;
    },
    clearSelectedWarningLetter: (state) => {
      state.selectedWarningLetter = null;
    },
    clearWarningLetterByPKS: (state) => { state.getWarningLettersByPKS = asyncState(); },
    clearCreateWarningLetter: (state) => { state.createWarningLetter = asyncState(); },
    clearUpdateWarningLetter: (state) => { state.updateWarningLetter = asyncState(); },
    clearDeleteWarningLetter: (state) => { state.deleteWarningLetter = asyncState(); },
    clearWorkflowStates: (state) => {
      state.issueWarningLetter     = asyncState();
      state.deliverWarningLetter   = asyncState();
      state.acknowledgeWarningLetter = asyncState();
      state.resolveWarningLetter   = asyncState();
      state.escalateWarningLetter  = asyncState();
    },
  },
  extraReducers(builder) {
    const map = [
      [getWarningLettersThunk,       "getWarningLetters"],
      [getWarningLetterByIdThunk,    "getWarningLetterById"],
      [getWarningLettersByPKSThunk,  "getWarningLettersByPKS"],
      [getWarningLettersByMitraThunk,"getWarningLettersByMitra"],
      [createWarningLetterThunk,     "createWarningLetter"],
      [updateWarningLetterThunk,     "updateWarningLetter"],
      [deleteWarningLetterThunk,     "deleteWarningLetter"],
      [issueWarningLetterThunk,      "issueWarningLetter"],
      [deliverWarningLetterThunk,    "deliverWarningLetter"],
      [acknowledgeWarningLetterThunk,"acknowledgeWarningLetter"],
      [resolveWarningLetterThunk,    "resolveWarningLetter"],
      [escalateWarningLetterThunk,   "escalateWarningLetter"],
    ];

    map.forEach(([thunk, key]) => {
      builder
        .addCase(thunk.pending,   (state) => { state[key] = pending(state[key]); })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key] = fulfilled(state[key], action.payload);
          // Auto-set selected on getById
          if (key === "getWarningLetterById") {
            state.selectedWarningLetter = action.payload?.data ?? action.payload;
          }
        })
        .addCase(thunk.rejected,  (state, action) => {
          state[key] = rejected(state[key], action.error.message);
        });
    });
  },
});

export const warningLetterAction = {
  ...warningLetterSlice.actions,
  getWarningLettersThunk,
  getWarningLetterByIdThunk,
  getWarningLettersByPKSThunk,
  getWarningLettersByMitraThunk,
  createWarningLetterThunk,
  updateWarningLetterThunk,
  deleteWarningLetterThunk,
  issueWarningLetterThunk,
  deliverWarningLetterThunk,
  acknowledgeWarningLetterThunk,
  resolveWarningLetterThunk,
  escalateWarningLetterThunk,
};

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectWarningLetter        = (state) => state.warningLetter;
export const selectWarningLetterForm    = (state) => state.warningLetter?.warningLetterForm;
export const selectSelectedWarningLetter = (state) => state.warningLetter?.selectedWarningLetter;
export const selectWarningLettersByPKS  = (state) =>
  state.warningLetter?.getWarningLettersByPKS?.data?.data ?? [];

export default warningLetterSlice.reducer;
